import React, { useState, useEffect } from 'react';
import {
  Box, Typography, Paper, Table, TableHead, TableBody, TableRow, TableCell,
  Button, Chip, makeStyles, CircularProgress, TableContainer, TextField
} from '@material-ui/core';
import TuneIcon from '@material-ui/icons/Tune';
import SwapHorizIcon from '@material-ui/icons/SwapHoriz';
import { useDispatch, useSelector } from 'react-redux';
import { getProductStocks } from 'src/features/productSlice';

const useStyles = makeStyles(theme => ({
  root: {
    position: 'relative',
    '&.MuiPaper-elevation1': {
      boxShadow: 'none'
    }
  },
  tableWrapper: {
    '& .MuiTableCell-root': {
      borderBottom: '2px solid #F1F1F1 !important',
      padding: '4px !important',
      fontSize: '14px'
    },
    overflow: 'auto',
    '&::-webkit-scrollbar': { width: '8px', height: '8px' },
    '&::-webkit-scrollbar-thumb': {
      backgroundColor: '#d2d9ddff',
      borderRadius: '8px'
    },
    '&::-webkit-scrollbar-thumb:hover': { backgroundColor: '#bfc6caff' },
    '&::-webkit-scrollbar-track': {
      backgroundColor: '#f1f1f1',
      borderRadius: '8px'
    }
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(2),
    marginBottom: theme.spacing(2),
    flexWrap: 'wrap'
  },
  filterSelect: {
    minWidth: 220,
    '& .MuiOutlinedInput-root': {
      height: 40,
      backgroundColor: '#ffffff',
      borderRadius: 4,
      '& fieldset': {
        borderColor: '#E5E5E8',
        top: 0
      },
      '&:hover fieldset': {
        borderColor: '#D9042F'
      },
      '&.Mui-focused fieldset': {
        borderColor: '#D9042F',
        borderWidth: '1px'
      }
    },
    '& .MuiOutlinedInput-input': {
      padding: '0 14px',
      fontSize: '14px',
      height: 40,
      boxSizing: 'border-box'
    }
  },
  availableGood: { color: '#27ae60', fontWeight: 700 },
  availableLow: { color: '#e67e22', fontWeight: 700 },
  availableEmpty: { color: '#e74c3c', fontWeight: 700 }
}));

const StockOverview = ({ products = [], agencies = [], canWrite, onAdjust, onTransfer }) => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const [selectedProductId, setSelectedProductId] = useState('');
  const { productStocks, isLoadingStock } = useSelector(state => state.productSlice);

  const selectedProduct = products.find(p => String(p.id) === String(selectedProductId));

  useEffect(() => {
    if (selectedProductId) {
      dispatch(getProductStocks({ id: selectedProductId, data: {} }));
    }
  }, [selectedProductId, dispatch]);

  const getAvailableClass = (stock) => {
    const avail = stock.available ?? (stock.quantity - (stock.reserved_quantity || 0));
    const threshold = selectedProduct?.stock_warning_threshold;
    if (avail === 0) return classes.availableEmpty;
    if (threshold && avail <= threshold) return classes.availableLow;
    return classes.availableGood;
  };

  return (
    <Box>
      {/* Selector & Actions Toolbar */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2} flexWrap="wrap" style={{ gap: 12 }}>
        <TextField
          className={classes.filterSelect}
          size="small"
          select
          value={selectedProductId}
          onChange={e => setSelectedProductId(e.target.value)}
          SelectProps={{
            native: true
          }}
          variant="outlined"
          style={{ width: '260px' }}
        >
          <option value="">-- Chọn sản phẩm --</option>
          {products.map(p => (
            <option key={p.id} value={p.id}>
              {`[${p.code}] ${p.name}`}
            </option>
          ))}
        </TextField>

        {canWrite && (
          <Box display="flex" style={{ gap: 8 }}>
            <Button
              variant="outlined"
              color="primary"
              startIcon={<SwapHorizIcon />}
              onClick={onTransfer}
              size="small"
              style={{ textTransform: 'none', height: '40px', borderRadius: 8, fontWeight: 600 }}
            >
              Chuyển kho
            </Button>
            <Button
              variant="outlined"
              color="primary"
              startIcon={<TuneIcon />}
              onClick={() => onAdjust(selectedProduct || null)}
              size="small"
              style={{ textTransform: 'none', height: '40px', borderRadius: 8, fontWeight: 600 }}
            >
              Điều chỉnh kho
            </Button>
          </Box>
        )}
      </Box>

      <TableContainer component={Paper} className={classes.root} style={{ maxWidth: '100%', overflowX: 'auto' }}>
        <Box className={classes.tableWrapper}>
          {!selectedProductId ? (
            <Box display="flex" flexDirection="column" justifyContent="center" alignItems="center" width="100%" py={6}>
              <Box mb={1}>
                <img src="/static/empty.png" alt="empty" style={{ height: 80, opacity: 0.6 }} />
              </Box>
              <Typography>
                <h4>Chọn sản phẩm để xem tồn kho theo từng đại lý</h4>
              </Typography>
            </Box>
          ) : isLoadingStock ? (
            <Box display="flex" justifyContent="center" py={6}>
              <CircularProgress color="primary" />
            </Box>
          ) : (
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell style={{ width: 50, textAlign: 'center' }}>STT</TableCell>
                  <TableCell>Đại lý</TableCell>
                  <TableCell style={{ width: 120, textAlign: 'center' }}>Số lượng</TableCell>
                  <TableCell style={{ width: 120, textAlign: 'center' }}>Đang giữ chỗ</TableCell>
                  <TableCell style={{ width: 120, textAlign: 'center' }}>Khả dụng</TableCell>
                  <TableCell style={{ width: 120, textAlign: 'center' }}>Trạng thái</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {productStocks.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} align="center" style={{ padding: 40, color: '#aaa' }}>
                      Chưa có tồn kho tại đại lý nào
                    </TableCell>
                  </TableRow>
                ) : (
                  productStocks.map((stock, idx) => {
                    const available = stock.available ?? (stock.quantity - (stock.reserved_quantity || 0));
                    const agencyName = stock.agency?.name || agencies.find(a => a.id === stock.agency_id)?.name || `Đại lý #${stock.agency_id}`;
                    return (
                      <TableRow hover key={stock.id || idx}>
                        <TableCell style={{ textAlign: 'center' }}>{idx + 1}</TableCell>
                        <TableCell>
                          <Typography style={{ fontWeight: 600, fontSize: 14 }}>{agencyName}</Typography>
                        </TableCell>
                        <TableCell align="center">
                          <Typography style={{ fontWeight: 600 }}>{stock.quantity}</Typography>
                        </TableCell>
                        <TableCell align="center">
                          <Typography style={{ color: '#e67e22', fontWeight: 600 }}>
                            {stock.reserved_quantity || 0}
                          </Typography>
                        </TableCell>
                        <TableCell align="center">
                          <Typography className={getAvailableClass(stock)}>
                            {available}
                          </Typography>
                        </TableCell>
                        <TableCell align="center">
                          {available === 0 ? (
                            <span className="badge" style={{ backgroundColor: '#F8D7DA', color: '#D9534F', padding: '2px 8px', borderRadius: 4, fontSize: 12, fontWeight: 700 }}>Hết hàng</span>
                          ) : available <= (selectedProduct?.stock_warning_threshold || 0) ? (
                            <span className="badge" style={{ backgroundColor: '#FFF3CD', color: '#856404', padding: '2px 8px', borderRadius: 4, fontSize: 12, fontWeight: 700 }}>Sắp hết</span>
                          ) : (
                            <span className="badge" style={{ backgroundColor: '#D4EDDA', color: '#155724', padding: '2px 8px', borderRadius: 4, fontSize: 12, fontWeight: 700 }}>Còn hàng</span>
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          )}
        </Box>
      </TableContainer>
    </Box>
  );
};

export default StockOverview;
